<template>
  <div class="sib-radio-group-wrapper">
    <template v-for="(option, i) in options" :key="i">
      <input
        type="radio"
        :id="`${group}${option.id}`"
        :name="group"
        :value="option.value"
        :checked="isActive(option.value)"
        @input="onChange"
      />
      <label
        :for="`${group}${option.id}`"
        class="label-wrapper"
        :class="{
          'label-wrapper--disable': option.disable,
          'label-wrapper--loading': props.loading,
        }"
      >
        <sib-image
          class="label-image"
          :class="{
            'label-image--active': isActive(option.value),
            'label-image--disable': option.disable,
            'label-image--loading': props.loading,
            'label-image--show-text': showText,
          }"
          :src="option.image"
          :alt="option.name"
          :width="option.width || imageLabelSize"
          :height="option.height || imageLabelSize"
          :title="option.name"
          :sources="option.sources"
        ></sib-image>
        <span v-if="showText" class="label-text">{{ option.name }}</span>
      </label>
    </template>
    <input
      v-if="propertyName"
      type="hidden"
      :name="`properties[${propertyName}]`"
      :value="`Image:`"
    />
    <input
      v-if="propertyName"
      type="hidden"
      :name="`properties[${
        attributeKey
          ? attributeKey + CUSTOM_IMAGE_LINK_PREFIX
          : CUSTOM_IMAGE_LINK_PREFIX
      } ${propertyName}]`"
      :value="currentValue"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { CUSTOM_IMAGE_LINK_PREFIX } from '@/utils'
import { ImageSource } from '@/types'

interface OptionRadio {
  id: any
  name: string
  value: string
  image?: string
  width?: number
  height?: number
  disable?: boolean
  sources?: Array<ImageSource>
}
interface OptionsProps {
  group: string
  options: OptionRadio[]
  type?: string
  modelValue?: string
  loading?: boolean
  propertyName?: string
  showText?: boolean
  attributeKey?: string
}

const emit = defineEmits(['update:modelValue'])

const props = withDefaults(defineProps<OptionsProps>(), {
  showText: true,
})

let currentValue = ref(props.modelValue)

const imageLabelSize = 56

function isActive(value: string) {
  return currentValue.value === value
}

function onChange(event: Event) {
  currentValue.value = (event.target as HTMLInputElement)?.value
  emit('update:modelValue', currentValue.value)
}
</script>

<style lang="scss">
.sib-radio-group-wrapper {
  .label-wrapper {
    position: relative;
    .label-text {
      position: absolute;
      max-width: 56px;
      font-size: 11px;
      left: 4px;
      bottom: -1.5rem;
      font-weight: normal;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &--disable {
      pointer-events: none;
      .label-text {
        opacity: 0.3;
      }
    }
    &--loading {
      pointer-events: none;
    }
  }
  .label-image {
    background-color: transparent;
    box-sizing: border-box;
    box-shadow: 0 0 2px 1px #0003;
    border: 3px solid #fff;
    margin: 5px 5px 3px 4px;
    &--show-text {
      margin: 5px 5px 20px 4px;
    }
    &--active {
      position: relative;
      border-color: #2f80ed;
      &::before {
        content: '';
        position: absolute;
        width: 12px;
        height: 12px;
        right: 2px;
        bottom: 0;
        background: url('@/assets/images/icons/check-mark.svg');
        background-repeat: no-repeat;
        background-size: cover;
        z-index: var(--base-z-index);
      }
    }
    &--disable {
      opacity: 0.3;
      pointer-events: none;
    }
    &--loading {
      pointer-events: none;
    }
    img {
      object-fit: cover;
      aspect-ratio: 1 / 1;
    }
  }
  input {
    display: none;
  }
  label {
    cursor: pointer;
  }
}
</style>
